/**
 * Utility functions for handling returnTo URL parameters.
 *
 * returnTo is user-editable and becomes a Back link href, so it is a
 * navigation sink: write it with addReturnTo and read it only through
 * getReturnTo (or useReturnTo, which wraps both).
 */

/**
 * Fixed origin used to resolve a returnTo value. `.invalid` is a reserved
 * TLD, so no real site can share it; anything that resolves to a different
 * origin was trying to leave the app. A constant (not window.location) keeps
 * getReturnTo pure and safe to call during SSR.
 */
const RETURN_TO_SENTINEL_ORIGIN = "https://return-to.invalid";

/**
 * Adds a returnTo parameter to a base URL
 * @param baseUrl - The base URL to add the parameter to
 * @param returnToUrl - The URL to return to (will be encoded)
 * @returns URL with returnTo parameter appended
 */
export function addReturnTo(baseUrl: string, returnToUrl: string): string {
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}returnTo=${encodeURIComponent(returnToUrl)}`;
}

/**
 * Resolves a value against the sentinel origin and returns the URL only if
 * it stays on that origin. Null means the value leaves the app or cannot be
 * parsed at all.
 */
function resolveOnSentinelOrigin(value: string): URL | null {
  let resolved: URL;
  try {
    resolved = new URL(value, RETURN_TO_SENTINEL_ORIGIN);
  } catch (error) {
    // Callers fall back to their default Back route on null, so the user
    // still has a working Back; warn so a malformed link stays traceable.
    console.warn("[returnTo] ignoring unparseable returnTo value", error);
    return null;
  }
  return resolved.origin === RETURN_TO_SENTINEL_ORIGIN ? resolved : null;
}

/**
 * Validates a returnTo value and returns it as a same-origin path.
 *
 * The input is used as-is: callers pass the value from the query-state
 * reader (nuqs / URLSearchParams), which has already decoded it once.
 * Decoding again would corrupt escaped characters in the destination's own
 * query (`%26` -> `&`) and turn `/%2Fevil.example` into `//evil.example`.
 *
 * A leading "/" is only a cheap prefilter. `//host`, `/\host` and
 * `/<tab>/host` all start with "/" yet a browser resolves them to another
 * host, so the value is accepted only if resolving it against a fixed origin
 * keeps that origin. Only pathname + search + hash are returned, and only
 * when the resolved pathname does not start with "//": dot segments are
 * resolved inside the sentinel origin, so `/..//host` or `/./\host` pass the
 * origin check yet leave a pathname of `//host`, which as an href is
 * protocol-relative. That covers `//return-to.invalid` too, which a second
 * origin check against the sentinel would accept.
 *
 * paramsToRemove drops whole `&`-separated segments whose decoded key is
 * named; the kept segments are not re-encoded, so the returned query is the
 * resolved query byte-for-byte minus the removed params (`%20` stays `%20`).
 *
 * @param returnToUrl - The decoded returnTo value (can be null)
 * @param paramsToRemove - Optional query parameter names to strip
 * @returns The same-origin path, or null if absent or rejected
 */
export function getReturnTo(
  returnToUrl: string | null,
  paramsToRemove?: string[]
): string | null {
  if (!returnToUrl || !returnToUrl.startsWith("/")) {
    return null;
  }

  const resolved = resolveOnSentinelOrigin(returnToUrl);
  if (!resolved) {
    return null;
  }

  // The returned string is the href the browser follows on the app's own
  // origin. The parser has already turned "\" into "/" and stripped tabs and
  // newlines, so the pathname starts with "/" and is protocol-relative
  // exactly when its next character is also "/". Re-resolving against the
  // sentinel would miss "//return-to.invalid", which keeps the sentinel's
  // origin yet leaves the app.
  if (resolved.pathname.startsWith("//")) {
    return null;
  }

  if (paramsToRemove && paramsToRemove.length > 0) {
    const removed = new Set(paramsToRemove);
    const kept = resolved.search
      .slice(1)
      .split("&")
      .filter((segment) => !removed.has(decodeQueryKey(segment)));
    const query = kept.join("&");
    const search = query ? `?${query}` : "";
    return `${resolved.pathname}${search}${resolved.hash}`;
  }

  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

/**
 * The name of one `&`-separated query segment, decoded the way
 * URLSearchParams decodes it (`+` is a space), so a key is matched whether it
 * is written plain or percent-encoded. A key that does not decode is returned
 * as written: it holds an invalid escape, so it cannot equal a plain param
 * name and its segment is kept.
 */
function decodeQueryKey(segment: string): string {
  const separator = segment.indexOf("=");
  const rawKey = separator === -1 ? segment : segment.slice(0, separator);
  try {
    return decodeURIComponent(rawKey.replaceAll("+", " "));
  } catch (error) {
    // The segment is kept unchanged, so the Back link still works; warn so
    // a malformed key stays traceable.
    console.warn("[returnTo] keeping a query key that does not decode", error);
    return rawKey;
  }
}
